/**
 * Plan from a photo — the ideas list (docs/CAPTURE_TO_PLAN_PLAN.md,
 * Decision 5).
 *
 * In memory only: no mirror in browser storage, so extracted highlights
 * never sit on disk and a reload drops the list. The list has an owner and a
 * ticket:
 *
 * - a capture response is applied only if its ticket is still the latest and
 *   the account session recorded when the request started is still current;
 *   the owner is then that session's user id;
 * - ownership follows the account: every user-store change runs
 *   `reconcile()` keyed on `user?.id` (as `utils/accountSession.ts` is), and
 *   logout or a switch to another account clears the list. A signed-out
 *   person never holds one.
 *
 * The capture response carries no token and this store keeps none.
 */
import { create } from 'zustand';
import { useUserStore } from './useUserStore';
import {
  currentWebAccountSession,
  isCurrentWebAccountSession,
  type WebAccountSession,
} from '../utils/accountSession';
import type {
  CaptureConfig, CaptureIdea, CaptureResponse, CaptureSourceKind,
} from '../services/plansChatApi';
import type { CaptureFailureReason } from '../hooks/useAnalytics';

export type CaptureStatus = 'idle' | 'loading' | 'done' | 'failed';

/** A request in flight: what `applyCapture*` needs to decide it is current. */
export interface CaptureTicket {
  ticket: number;
  session: WebAccountSession;
}

interface CaptureState {
  owner: number | null;
  ticket: number;
  status: CaptureStatus;
  ideas: CaptureIdea[];
  sourceKind: CaptureSourceKind | null;
  /** Indices into `ideas` the owner has already started a plan from. */
  planned: number[];
  failure: CaptureFailureReason | null;
  /** The signed-out prompt (Decision 4) is open. */
  signUpPrompt: boolean;

  /** The config probe's answer; not account-owned (every caller gets it). */
  config: CaptureConfig | null;

  begin: () => CaptureTicket;
  applyCaptureResponse: (request: CaptureTicket, response: CaptureResponse) => boolean;
  applyCaptureFailure: (request: CaptureTicket, reason: CaptureFailureReason) => boolean;
  /** A client-side refusal (over the file caps): nothing was sent. */
  rejectLocally: (reason: CaptureFailureReason) => void;
  markPlanned: (index: number) => void;
  setSignUpPrompt: (open: boolean) => void;
  clear: () => void;
  reconcile: (userId: number | null) => void;
  setConfig: (config: CaptureConfig | null) => void;
}

const EMPTY = {
  owner: null,
  status: 'idle' as CaptureStatus,
  ideas: [] as CaptureIdea[],
  sourceKind: null,
  planned: [] as number[],
  failure: null,
  signUpPrompt: false,
};

let lastUserId: number | null = useUserStore.getState().user?.id ?? null;

export const useCaptureStore = create<CaptureState>()((set, get) => {
  const isCurrent = (request: CaptureTicket) => (
    request.ticket === get().ticket && isCurrentWebAccountSession(request.session)
  );
  return {
    ...EMPTY,
    ticket: 0,
    config: null,

    begin: () => {
      const ticket = get().ticket + 1;
      set({ ...EMPTY, ticket, status: 'loading' });
      return { ticket, session: currentWebAccountSession() };
    },

    applyCaptureResponse: (request, response) => {
      if (!isCurrent(request)) return false;
      set({
        owner: request.session.userId,
        status: 'done',
        ideas: Array.isArray(response.ideas) ? response.ideas : [],
        sourceKind: response.source_kind,
        planned: [],
        failure: null,
      });
      return true;
    },

    applyCaptureFailure: (request, reason) => {
      if (!isCurrent(request)) return false;
      set({ ...EMPTY, status: 'failed', failure: reason });
      return true;
    },

    rejectLocally: (reason) => set({
      ...EMPTY, ticket: get().ticket + 1, status: 'failed', failure: reason,
    }),

    setSignUpPrompt: (open) => set({ signUpPrompt: open }),

    markPlanned: (index) => {
      const { planned } = get();
      if (!planned.includes(index)) set({ planned: [...planned, index] });
    },

    // The ticket is left alone. A response still in flight across an account
    // change is refused by the session check (the only guard for that case),
    // and the panel offers Clear only when nothing is in flight.
    clear: () => set({ ...EMPTY }),

    reconcile: (userId) => {
      if (userId === lastUserId) return;
      lastUserId = userId;
      get().clear();
    },

    setConfig: (config) => set({ config }),
  };
});

useUserStore.subscribe((state) => {
  useCaptureStore.getState().reconcile(state.user?.id ?? null);
});
